import { useState, useEffect } from 'react'

interface PlatformStats {
  totalCompanies: number
  totalUsers: number
  totalContent: number
  presentationCount: number
  pdfCount: number
  imageCount: number
  videoCount: number
}

interface UsePlatformStatsResult {
  stats: PlatformStats | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export function usePlatformStats(): UsePlatformStatsResult {
  const [stats, setStats] = useState<PlatformStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Fetch all companies
      const companiesResponse = await fetch('http://localhost:3000/api/admin/companies', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!companiesResponse.ok) {
        throw new Error('Failed to fetch companies')
      }

      const companiesData = await companiesResponse.json()
      const companies = companiesData.companies || []

      // Calculate total users from all companies
      const totalUsers = companies.reduce((sum: number, company: any) => {
        return sum + (company._count?.users || 0)
      }, 0)

      // Fetch all content (super admin sees everything)
      const contentResponse = await fetch('http://localhost:3000/api/content', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!contentResponse.ok) {
        throw new Error('Failed to fetch content statistics')
      }

      const contentData = await contentResponse.json()
      const contentItems = contentData.content || []

      // Calculate content stats
      const presentationCount = contentItems.filter((item: any) => item.type === 'presentation').length
      const pdfCount = contentItems.filter((item: any) => item.type === 'pdf').length
      const imageCount = contentItems.filter((item: any) => item.type === 'image').length
      const videoCount = contentItems.filter((item: any) => item.type === 'video').length

      setStats({
        totalCompanies: companies.length,
        totalUsers,
        totalContent: contentItems.length,
        presentationCount,
        pdfCount,
        imageCount,
        videoCount
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching platform stats:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  return { stats, loading, error, refetch: fetchStats }
}

export type { PlatformStats, UsePlatformStatsResult }
